import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import SEO from '@/components/SEO';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-3">{title}</h2>
      <div className="text-gray-700 dark:text-zinc-300 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

function Sub({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="font-semibold text-gray-800 dark:text-zinc-200 mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export default function PlatformGuidePage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950">
      <SEO
        title="How Open Card Vault Works"
        description="A comprehensive guide to Open Card Vault — accounts, collections, marketplace, auctions, trades, forums, achievements, and safety."
        url="/guide"
      />
      <div className="bg-gradient-to-r from-brand-600 to-accent-600 text-white py-12">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-7 w-7" />
            <h1 className="text-3xl font-display font-bold">How Open Card Vault Works</h1>
          </div>
          <p className="text-brand-100">A complete guide to the platform</p>
        </div>
      </div>

      <div className="container-custom py-10 max-w-3xl">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-8">

          <p className="text-gray-700 dark:text-zinc-300 mb-8 leading-relaxed">
            Open Card Vault is a marketplace and community platform for trading-card collectors.
            This guide explains how the platform works — from creating an account to buying,
            selling, trading, and connecting with other collectors.
          </p>

          <Section title="Accounts and Age Access">
            <p>
              Open Card Vault is open to users aged 13 and older. All users must provide their
              date of birth when registering.
            </p>
            <Sub title="Users aged 13–17">
              <p>Minor accounts have access to non-transactional features, including:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Browsing cards and listings</li>
                <li>Managing a personal collection</li>
                <li>Building a wishlist</li>
                <li>Creating and organizing decks</li>
                <li>Participating in the community forum</li>
                <li>Earning XP and achievements</li>
                <li>Maintaining a public profile</li>
              </ul>
              <p>
                Minor accounts may not buy, sell, trade, create marketplace listings, place bids,
                or conduct financial transactions of any kind on the platform.
              </p>
            </Sub>
            <Sub title="Users aged 18 and older">
              <p>Adult accounts have access to all platform features, including the full marketplace:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Creating and managing marketplace listings</li>
                <li>Buying cards directly or through auctions</li>
                <li>Making and receiving purchase offers</li>
                <li>Conducting card-for-card trades</li>
                <li>Connecting payment and payout accounts</li>
                <li>Receiving seller payouts</li>
              </ul>
            </Sub>
          </Section>

          <Section title="Your Profile">
            <p>
              Every account has a public profile page. Your profile displays your username,
              avatar, seller and buyer ratings, progression level, and activity on the platform.
            </p>
            <p>
              The <strong>Peek into the Vault</strong> section of your profile lets other users
              browse items from your collection you have chosen to highlight — including your
              Display Case, Bundle Cache, and Merch Gallery. Items marked for sale, for trade,
              or as open to offers are visible to visitors.
            </p>
          </Section>

          <Section title="Collections">
            <p>
              Your collection is a personal inventory of the cards and items you own. You can
              add individual cards, booster packs, booster boxes, bundles, and merchandise.
            </p>
            <p>When adding an item you can:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Record the condition</li>
              <li>Upload photographs</li>
              <li>Set the quantity</li>
              <li>Mark it as for sale, for trade, or open to offers</li>
              <li>Feature it on your public profile</li>
            </ul>
            <p>
              Your collection is a record of what you own — it is separate from your active
              marketplace listings.
            </p>
          </Section>

          <Section title="Wishlists">
            <p>
              Your wishlist is a list of cards you are looking for. Adding a card to your
              wishlist allows you to track it and helps the platform notify you when a matching
              listing becomes available.
            </p>
            <p>
              When a card on your wishlist is listed by another user, you will receive a
              notification. The forum also automatically generates discussion threads for cards
              with high wishlist demand, making it easier to connect with potential sellers or
              traders.
            </p>
          </Section>

          <Section title="Deck Builder">
            <p>
              The deck builder allows you to create and manage card decks. You can organize
              decks by game, name them, and track which cards belong to each build. Decks are
              stored privately in your account.
            </p>
          </Section>

          <Section title="Marketplace Listings">
            <p>
              Users 18 and older can create marketplace listings to sell cards and other
              collectibles. When creating a listing you will provide:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>A title and description</li>
              <li>Photographs of the actual item</li>
              <li>Condition</li>
              <li>Price (or auction settings)</li>
              <li>Shipping details</li>
            </ul>
            <p>
              Listings should accurately describe the item. Misrepresenting condition,
              authenticity, or other material details is prohibited under the Community
              Guidelines.
            </p>
          </Section>

          <Section title="Buying">
            <p>
              Users 18 and older can purchase items listed on the marketplace. You can browse
              listings by game, set, condition, price, and other filters.
            </p>
            <Sub title="Buy Now">
              <p>
                Fixed-price listings can be purchased directly. After completing checkout your
                order is confirmed and the seller is notified to ship.
              </p>
            </Sub>
            <Sub title="Making an Offer">
              <p>
                Some listings allow buyers to submit an offer below the listed price. The seller
                can accept, decline, or counter the offer. An accepted offer creates a binding
                transaction.
              </p>
            </Sub>
            <p>
              Payments are processed securely. Open Card Vault does not store payment card
              details directly.
            </p>
          </Section>

          <Section title="Selling">
            <p>
              Sellers receive payment after a buyer completes a purchase. Payouts are processed
              through a connected payout account. Sellers are responsible for shipping items
              promptly and accurately as described in the listing.
            </p>
            <p>
              Seller ratings are visible on your public profile and are based on completed
              transactions and buyer reviews.
            </p>
          </Section>

          <Section title="Auctions">
            <p>
              Sellers can create auction-format listings with a starting bid, optional reserve
              price, and a set end time.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Bidders must be 18 or older</li>
              <li>The highest bid at closing wins</li>
              <li>Reserve prices are not disclosed to bidders</li>
              <li>Shill bidding and auction manipulation are prohibited</li>
              <li>Winning bidders are obligated to complete the purchase</li>
            </ul>
            <p>
              Sellers are obligated to complete the sale when a reserve price is met or no
              reserve was set.
            </p>
          </Section>

          <Section title="Card-for-Card Trades">
            <p>
              Users 18 and older can propose card-for-card trades with other users. A trade
              proposal specifies which items each party would send. Both parties must agree
              before a trade is considered final.
            </p>
            <p>
              All trades should be conducted through the platform. Moving trades off-platform
              to avoid platform protections is discouraged and may violate the Community
              Guidelines.
            </p>
          </Section>

          <Section title="Reviews and Ratings">
            <p>
              After a completed transaction, buyers and sellers can leave reviews for each
              other. Reviews contribute to your seller and buyer ratings, which are displayed
              on your public profile.
            </p>
            <p>
              Reviews should be honest and based on the actual transaction. Fake, retaliatory,
              or manipulated reviews are prohibited.
            </p>
          </Section>

          <Section title="Community Forum">
            <p>
              The forum is a space for collectors to discuss cards, share pulls, find trades,
              and connect with others. The forum is organized into categories by topic and game.
            </p>
            <Sub title="Beginner's Guide">
              <p>
                The Beginner's Guide section contains staff-authored articles covering essential
                collecting knowledge — condition grading, storage, pricing, and more. These
                articles are open to community comments and discussion.
              </p>
            </Sub>
            <Sub title="Weekly Threads">
              <p>
                The platform automatically creates pinned weekly pull and trade threads each
                Monday. These are community spaces to share recent activity.
              </p>
            </Sub>
            <Sub title="Wishlist Match Threads">
              <p>
                When a significant number of collectors add the same card to their wishlists,
                the platform automatically generates a forum thread to help connect buyers
                and sellers.
              </p>
            </Sub>
            <Sub title="Card Mentions">
              <p>
                In forum posts, you can reference a card using the <code>[[Card Name]]</code>{' '}
                syntax. Hovering over the mention shows current listing prices and wishlist
                demand for that card.
              </p>
            </Sub>
            <p>
              Forum threads are automatically archived after 30 days of inactivity to keep
              the community space manageable. Pinned and article threads are not archived.
            </p>
          </Section>

          <Section title="Achievements and XP">
            <p>
              Open Card Vault includes a progression system that rewards participation.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Earn XP by posting in the forum, listing cards, completing trades, and other activities</li>
              <li>XP contributes to your level, which is displayed on your profile</li>
              <li>Trophies are awarded for reaching milestones and completing specific achievements</li>
              <li>A leaderboard tracks top contributors across the community</li>
            </ul>
            <p>
              XP and achievement systems are intended to recognize genuine participation.
              Attempting to artificially inflate XP or achievements is prohibited.
            </p>
          </Section>

          <Section title="Reporting and Disputes">
            <Sub title="Reporting Content">
              <p>
                A <strong>Report</strong> button is available on listings, profiles, forum posts,
                and messages. Reports are reviewed by the moderation team. Reporters are kept
                confidential. Do not submit false or retaliatory reports.
              </p>
            </Sub>
            <Sub title="Transaction Disputes">
              <p>
                If a transaction does not go as expected — for example, an item is not shipped,
                arrives significantly different from the listing, or a seller is unresponsive —
                contact support at{' '}
                <a href="mailto:support@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                  support@opencardvault.com
                </a>.
              </p>
              <p>
                Preserve all communication, photographs, and shipping records. These will help
                when investigating a dispute.
              </p>
            </Sub>
            <Sub title="Moderation">
              <p>
                The moderation team reviews reports and may remove content, restrict features,
                suspend accounts, or permanently terminate accounts depending on the nature
                and severity of a violation. Serious violations — including fraud and exploitation
                of minors — may be reported to appropriate authorities.
              </p>
            </Sub>
          </Section>

          <Section title="Safety">
            <ul className="list-disc pl-5 space-y-1">
              <li>Open Card Vault will never ask for your password by email or message</li>
              <li>Do not share your account credentials with anyone</li>
              <li>Be cautious of requests to move transactions off-platform</li>
              <li>Use tracking and insurance for high-value shipments</li>
              <li>Report suspicious behavior using the Report button</li>
              <li>If you believe your account has been compromised, contact support immediately</li>
            </ul>
            <p>
              Minors should never share personal information — including address, phone number,
              school, or financial details — with other users.
            </p>
          </Section>

          <Section title="Contact and Support">
            <p>
              For questions, issues, or appeals, you can reach the Open Card Vault team at:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                General support:{' '}
                <a href="mailto:support@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                  support@opencardvault.com
                </a>
              </li>
              <li>
                Privacy inquiries:{' '}
                <a href="mailto:privacy@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                  privacy@opencardvault.com
                </a>
              </li>
              <li>
                Legal matters:{' '}
                <a href="mailto:legal@opencardvault.com" className="text-brand-600 dark:text-brand-400 hover:underline">
                  legal@opencardvault.com
                </a>
              </li>
            </ul>
          </Section>

          <div className="border-t border-gray-200 dark:border-zinc-800 pt-6 mt-8 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-zinc-500">
            <Link to="/legal/privacy" className="hover:text-brand-600 dark:hover:text-brand-400">Privacy Policy</Link>
            <Link to="/legal/terms" className="hover:text-brand-600 dark:hover:text-brand-400">Terms of Service</Link>
            <Link to="/legal/community-guidelines" className="hover:text-brand-600 dark:hover:text-brand-400">Community Guidelines</Link>
            <Link to="/contact" className="hover:text-brand-600 dark:hover:text-brand-400">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
